import * as React from "react";
import { CheckIcon, type Icon } from "@phosphor-icons/react";
import { cn } from "@/zapi-frontend/shared/lib/utils";

import { DropdownMenuItem, DropdownMenuLabel } from "@libs/zapi-ui/ds/components/dropdown-menu";
import { Switch } from "@libs/zapi-ui/ds/components/switch";

function RichMenuLabel({ children }: { children: React.ReactNode }) {
  return (
    <DropdownMenuLabel className="typo-overline px-2.5 pt-3 pb-1.5 text-muted-foreground">{children}</DropdownMenuLabel>
  );
}

function RichMenuItem({
  icon: EntryIcon,
  title,
  description,
  trailing,
  selected = false,
  onClick
}: {
  icon: Icon;
  title: string;
  description?: string;
  trailing?: React.ReactNode;
  selected?: boolean;
  onClick?: () => void;
}) {
  return (
    <DropdownMenuItem
      onClick={onClick}
      aria-current={selected || undefined}
      className="gap-3 rounded-lg px-2.5 py-2 aria-current:bg-accent/60"
    >
      <span
        className={cn(
          "flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground group-focus/dropdown-menu-item:bg-background group-focus/dropdown-menu-item:text-foreground",
          selected && "bg-primary-100 text-primary-text dark:bg-primary-950"
        )}
      >
        <EntryIcon weight={selected ? "fill" : "regular"} className="size-[18px]" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="typo-label-md text-foreground">{title}</span>
        {description && <span className="typo-caption truncate text-muted-foreground">{description}</span>}
      </span>
      {trailing}
      {selected && !trailing && <CheckIcon aria-hidden="true" className="size-4 shrink-0 text-foreground" />}
    </DropdownMenuItem>
  );
}

function RichMenuSwitch({
  icon: EntryIcon,
  title,
  description,
  checked,
  onCheckedChange,
  ...props
}: {
  icon: Icon;
  title: string;
  description?: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
} & Omit<React.ComponentProps<typeof Switch>, "checked" | "onCheckedChange">) {
  return (
    <label
      data-slot="rich-menu-switch"
      className="flex cursor-pointer items-center gap-3 rounded-lg px-2.5 py-2 hover:bg-accent"
    >
      <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
        <EntryIcon weight={checked ? "fill" : "regular"} className="size-[18px]" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="typo-label-md text-foreground">{title}</span>
        {description && <span className="typo-caption line-clamp-2 text-muted-foreground">{description}</span>}
      </span>
      <Switch checked={checked} onCheckedChange={onCheckedChange} {...props} />
    </label>
  );
}

export { RichMenuItem, RichMenuLabel, RichMenuSwitch };
